import { useSyncExternalStore } from 'react';

export type PhoebeChatDebugFocusTarget = 'run-page' | 'sidebar-chat';

export type PhoebeChatDebugTimelineFocus = {
    readonly eventId: string | null;
    // Scopes a request to one run so only the surface showing that run
    // opens its debug rail — a run page and the docked sidebar chat can
    // both be mounted at once.
    readonly runId: string | null;
    readonly requestId: number;
    readonly target: PhoebeChatDebugFocusTarget | null;
};

const listeners = new Set<() => void>();
const EMPTY_FOCUS: PhoebeChatDebugTimelineFocus = {
    eventId: null,
    runId: null,
    requestId: 0,
    target: null,
};
let nextRequestId = 0;
let focus = EMPTY_FOCUS;

export function requestDebugTimelineFocus(
    eventId: string,
    runId: string | null = null,
    target: PhoebeChatDebugFocusTarget | null = null,
): void {
    nextRequestId += 1;
    focus = {
        eventId,
        runId,
        requestId: nextRequestId,
        target,
    };
    for (const listener of listeners) {
        listener();
    }
}

export function consumeDebugTimelineFocus(requestId: number): void {
    if (focus.requestId !== requestId || focus.eventId === null) {
        return;
    }
    focus = {
        eventId: null,
        runId: null,
        requestId,
        target: null,
    };
    for (const listener of listeners) {
        listener();
    }
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getFocus(): PhoebeChatDebugTimelineFocus {
    return focus;
}

export function usePhoebeChatDebugTimelineFocus(): PhoebeChatDebugTimelineFocus {
    return useSyncExternalStore(subscribe, getFocus, getFocus);
}
